import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { ApiResponse } from '../../../../interfaces/models/models/paginated-response.model';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { ISuppliers } from '../../../../interfaces/suppliers/ISuppliers';
import { ISuppliersListState } from '../../../../interfaces/suppliers/ISuppliersList';
import { SuppliersService } from './SuppliersApi';


const initialState: ISuppliersListState = {
    supplierIsLoading: false,
    suppliersAreLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchSuppliers = createAsyncThunk<any, IPaginationPayload>(
    'partners/tryToFetchSuppliers',
    async ({ pageNumber, pageSize, data }) => {
        const result = await SuppliersService.tryToFetchSuppliers(pageNumber, pageSize, data);
        return result;
    },
);

export const tryToFetchAllSuppliers = createAsyncThunk<ISuppliers, undefined>(
    'suppliers/tryToFetchAllSuppliers',
    async () => {
        const result = await SuppliersService.tryToFetchAllSuppliers();
        return result?.data?.data;
    },
);

export const tryToFetchSingleSuppliers = createAsyncThunk<ISuppliers, string>(
    'suppliers/tryToFetchSingleSuppliers',
    async (id: string) => {
        const result = await SuppliersService.tryToFetchSingleSuppliers(id);
        return result?.data?.data;
    },
);

export const tryToCreateSuppliers = createAsyncThunk<ApiResponse<ISuppliers>, any>(
    'suppliers/tryToCreateSuppliers',
    async (data: ISuppliers, { rejectWithValue }) => {
        try {
            const result = await SuppliersService.tryToCreateSuppliers(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEditSuppliers = createAsyncThunk<ApiResponse<ISuppliers>, { id: string, data: any }>(
    'suppliers/tryToEditSuppliers',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const result = await SuppliersService.tryToEditSuppliers(id, data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEnableDisableSuppliers = createAsyncThunk<ApiResponse<any>, { id: string, status: boolean }>(
    'suppliers/tryToEnableDisableSuppliers',
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const result = await SuppliersService.tryToEnableDisableSuppliers(id, status);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToDeleteSuppliers = createAsyncThunk<ApiResponse<ISuppliers>, string>(
    'suppliers/tryToDeleteSuppliers',
    async (id, { rejectWithValue }) => {
        try {
            const result = await SuppliersService.tryToDeleteSuppliers(id);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToFetchAllPartners = createAsyncThunk<any, any>(
    'partners/tryToFetchAllPartners',
    async () => {
        const result = await SuppliersService.tryToFetchAllPartners();
        return result?.data;
    },
);

export const suppliersSlice = createSlice({
    name: 'suppliers',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchSuppliers
            .addCase(tryToFetchSuppliers.pending, (state) => {
                state.suppliersAreLoading = true;
            })
            .addCase(tryToFetchSuppliers.rejected, (state) => {
                state.suppliersAreLoading = false;
            })
            .addCase(tryToFetchSuppliers.fulfilled, (state, action) => {
                state.suppliersAreLoading = false;
                state.suppliers = action.payload;
            })

            // tryToFetchSingleSuppliers
            .addCase(tryToFetchSingleSuppliers.pending, (state) => {
                state.supplierIsLoading = true;
            })
            .addCase(tryToFetchSingleSuppliers.rejected, (state) => {
                state.supplierIsLoading = false;
            })
            .addCase(tryToFetchSingleSuppliers.fulfilled, (state, action) => {
                state.supplierIsLoading = false;
                state.supplier = action.payload;
            })

            // tryToCreateSuppliers
            .addCase(tryToCreateSuppliers.pending, (state) => {
                state.supplierIsLoading = true;
            })
            .addCase(tryToCreateSuppliers.rejected, (state) => {
                state.supplierIsLoading = false;
            })
            .addCase(tryToCreateSuppliers.fulfilled, (state, action) => {
                state.supplierIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg;
                if (state.suppliers?.data) state.suppliers.data.elements = [...state.suppliers?.data.elements, data];
            })

            // tryToEditSuppliers
            .addCase(tryToEditSuppliers.pending, (state) => {
                state.supplierIsLoading = true;
            })
            .addCase(tryToEditSuppliers.rejected, (state) => {
                state.supplierIsLoading = false;
            })
            .addCase(tryToEditSuppliers.fulfilled, (state) => {
                state.supplierIsLoading = false;
            })

            // tryToEnableDisableSuppliers
            .addCase(tryToEnableDisableSuppliers.pending, (state) => {
                state.supplierIsLoading = true;
            })
            .addCase(tryToEnableDisableSuppliers.rejected, (state) => {
                state.supplierIsLoading = false;
            })
            .addCase(tryToEnableDisableSuppliers.fulfilled, (state, action) => {
                state.supplierIsLoading = false;
                state.requestStatus = 'success';
                if (state.suppliers?.data.elements)
                    state.suppliers.data.elements =
                        { ...state }.suppliers?.data.elements.map((supplier) => {
                            if (supplier?._id === action.meta?.arg?.id) {
                                return {
                                    ...supplier,
                                    enabled: action?.meta?.arg?.status
                                }
                            } else {
                                return supplier
                            }
                        }) || [];
            })

            // tryToDeleteSuppliers
            .addCase(tryToDeleteSuppliers.pending, (state) => {
                state.supplierIsLoading = true;
            })
            .addCase(tryToDeleteSuppliers.rejected, (state) => {
                state.supplierIsLoading = false;
            })
            .addCase(tryToDeleteSuppliers.fulfilled, (state, action) => {
                state.supplierIsLoading = false;
                state.requestStatus = 'success';
                if (state.suppliers?.data.elements)
                    state.suppliers.data.elements = state.suppliers?.data.elements.filter((supplier) => supplier?._id !== action.meta?.arg)
            })
    },
});

export default suppliersSlice.reducer;